import { createMaintainerProClient } from "./maintainer-pro";
import { startChatRealtime } from "./realtime";
import { createAiUiLogger } from "./log";

const unreadLog = createAiUiLogger("unread");

export type ChatUnreadSourceOptions = {
  apiUrl?: string;
  maintainerProUrl?: string;
  maintainerProApiKey?: string;
  conversationId?: string;
  /** localStorage key for last-read watermark (default derived from conversation). */
  storageKey?: string;
};

function defaultStorageKey(conversationId?: string): string {
  return `ai-ui:chat-read:${conversationId ?? "default"}`;
}

export function resolveUnreadStorageKey(
  options: Pick<ChatUnreadSourceOptions, "storageKey" | "conversationId">
): string {
  return options.storageKey ?? defaultStorageKey(options.conversationId);
}

export function readLastReadAt(storageKey: string): string | null {
  if (typeof window === "undefined") return null;
  try {
    return window.localStorage.getItem(storageKey);
  } catch {
    return null;
  }
}

export function writeLastReadAt(storageKey: string, iso: string): void {
  if (typeof window === "undefined") return;
  try {
    window.localStorage.setItem(storageKey, iso);
  } catch {
    // ignore
  }
}

export type StoredChatMessage = {
  id?: string;
  role: string;
  content: string;
  provider?: string | null;
  senderType?: string | null;
  senderName?: string | null;
  parentMessageId?: string | null;
  queueStatus?: string | null;
  queuePosition?: number | null;
  createdAt?: string;
};

function mpCacheKey(baseUrl: string, apiKey: string): string {
  return `${baseUrl.replace(/\/$/, "")}|${apiKey}`;
}

/** Resolved Maintainer Pro conversation ids (avoids listConversations on every poll). */
const resolvedConversationIds = new Map<string, string>();

function conversationIdStorageKey(baseUrl: string, apiKey: string): string {
  return `ai-ui:mp-conversation:${mpCacheKey(baseUrl, apiKey)}`;
}

function readCachedConversationId(
  baseUrl: string,
  apiKey: string
): string | null {
  const key = mpCacheKey(baseUrl, apiKey);
  const mem = resolvedConversationIds.get(key);
  if (mem) return mem;
  if (typeof window === "undefined") return null;
  try {
    return window.sessionStorage.getItem(
      conversationIdStorageKey(baseUrl, apiKey)
    );
  } catch {
    return null;
  }
}

export function rememberConversationId(
  baseUrl: string,
  apiKey: string,
  conversationId: string
): void {
  if (!conversationId || conversationId === "shared") return;
  const key = mpCacheKey(baseUrl, apiKey);
  resolvedConversationIds.set(key, conversationId);
  if (typeof window === "undefined") return;
  try {
    window.sessionStorage.setItem(
      conversationIdStorageKey(baseUrl, apiKey),
      conversationId
    );
  } catch {
    // ignore
  }
}

async function resolveMaintainerProConversationId(
  baseUrl: string,
  apiKey: string,
  conversationId?: string
): Promise<string | null> {
  if (conversationId && conversationId !== "shared") {
    rememberConversationId(baseUrl, apiKey, conversationId);
    return conversationId;
  }

  const cached = readCachedConversationId(baseUrl, apiKey);
  if (cached) return cached;

  const mp = createMaintainerProClient({ baseUrl, apiKey });
  const conversations = await mp.listConversations();
  const id = conversations[0]?.id ?? null;
  if (id) rememberConversationId(baseUrl, apiKey, id);
  return id;
}

/** Full stored messages for live thread sync (messages endpoint only). */
export async function fetchChatMessages(
  options: ChatUnreadSourceOptions
): Promise<StoredChatMessage[]> {
  const {
    apiUrl = "/api/chat",
    maintainerProUrl,
    maintainerProApiKey,
    conversationId,
  } = options;

  if (maintainerProUrl && maintainerProApiKey) {
    const id = await resolveMaintainerProConversationId(
      maintainerProUrl,
      maintainerProApiKey,
      conversationId
    );
    if (!id) return [];
    const mp = createMaintainerProClient({
      baseUrl: maintainerProUrl,
      apiKey: maintainerProApiKey,
    });
    const messages = await mp.listMessages(id);
    return messages.map((m) => ({
      id: m.id,
      role: m.role,
      content: m.content ?? "",
      provider: m.provider,
      senderType: m.senderType,
      senderName: m.senderName,
      createdAt: m.createdAt,
    }));
  }

  const res = await fetch(apiUrl);
  if (!res.ok) return [];
  const data = (await res.json().catch(() => ({}))) as {
    messages?: StoredChatMessage[];
  };
  return (data.messages ?? []).map((m) => ({
    id: m.id,
    role: m.role,
    content: m.content ?? "",
    provider: m.provider,
    senderType: m.senderType,
    senderName: m.senderName,
    createdAt: m.createdAt,
  }));
}

/**
 * Lightweight timestamps for unread badges.
 * Prefer conversation summaries (one call) when Maintainer Pro is configured.
 */
export async function fetchChatMessageTimestamps(
  options: ChatUnreadSourceOptions
): Promise<string[]> {
  const {
    apiUrl = "/api/chat",
    maintainerProUrl,
    maintainerProApiKey,
    conversationId,
  } = options;

  if (maintainerProUrl && maintainerProApiKey) {
    const cached = readCachedConversationId(
      maintainerProUrl,
      maintainerProApiKey
    );
    const knownId =
      conversationId && conversationId !== "shared"
        ? conversationId
        : cached;

    if (knownId) {
      // Single messages call when we already know the conversation.
      const messages = await fetchChatMessages({
        ...options,
        conversationId: knownId,
      });
      return messages
        .map((m) => m.createdAt)
        .filter((t): t is string => Boolean(t));
    }

    // One listConversations call — summaries include createdAt.
    const mp = createMaintainerProClient({
      baseUrl: maintainerProUrl,
      apiKey: maintainerProApiKey,
    });
    const conversations = await mp.listConversations();
    const first = conversations[0];
    if (first?.id) {
      rememberConversationId(
        maintainerProUrl,
        maintainerProApiKey,
        first.id
      );
    }
    return (first?.messages ?? [])
      .map((m) => m.createdAt)
      .filter((t): t is string => Boolean(t));
  }

  const res = await fetch(apiUrl);
  if (!res.ok) return [];
  const data = (await res.json().catch(() => ({}))) as {
    messages?: Array<{ createdAt?: string }>;
  };
  return (data.messages ?? [])
    .map((m) => m.createdAt)
    .filter((t): t is string => Boolean(t));
}

export function countUnreadSince(
  timestamps: string[],
  lastReadAt: string | null
): number {
  if (!lastReadAt) return 0;
  const watermark = Date.parse(lastReadAt);
  if (Number.isNaN(watermark)) return 0;
  return timestamps.filter((t) => {
    const ms = Date.parse(t);
    return !Number.isNaN(ms) && ms > watermark;
  }).length;
}

export function latestTimestamp(timestamps: string[]): string | null {
  let latest: string | null = null;
  let latestMs = -Infinity;
  for (const t of timestamps) {
    const ms = Date.parse(t);
    if (!Number.isNaN(ms) && ms >= latestMs) {
      latestMs = ms;
      latest = t;
    }
  }
  return latest;
}

export type ChatUnreadPoller = {
  stop: () => void;
  /** Refresh unread count immediately. */
  refresh: () => Promise<void>;
  /** Mark everything currently known as read. */
  markRead: () => Promise<void>;
};

/**
 * Watch for new chat messages while the panel is closed.
 * Uses Maintainer Pro WebSocket when configured; otherwise a local HTTP poll.
 * First successful fetch seeds the read watermark so existing history is not unread.
 */
export function startChatUnreadPoller(
  options: ChatUnreadSourceOptions & {
    isOpen: () => boolean;
    onChange: (unreadCount: number) => void;
    pollIntervalMs?: number;
  }
): ChatUnreadPoller {
  const storageKey = resolveUnreadStorageKey(options);
  const pollIntervalMs = options.pollIntervalMs ?? 5000;
  let stopped = false;
  let timer: ReturnType<typeof setInterval> | null = null;
  let realtime: { stop: () => void } | null = null;

  unreadLog.debug(
    {
      storageKey,
      pollIntervalMs,
      mp: Boolean(options.maintainerProUrl && options.maintainerProApiKey),
      conversationId: options.conversationId,
    },
    "poller start"
  );

  const setCount = (count: number) => {
    if (!stopped) options.onChange(count);
  };

  const refresh = async () => {
    if (stopped) return;
    // While open, live sync owns fetching — don't double-hit the API.
    if (options.isOpen()) {
      setCount(0);
      return;
    }
    try {
      const timestamps = await fetchChatMessageTimestamps(options);
      const latest = latestTimestamp(timestamps);
      let lastRead = readLastReadAt(storageKey);

      if (!lastRead) {
        lastRead = latest ?? new Date().toISOString();
        writeLastReadAt(storageKey, lastRead);
        setCount(0);
        return;
      }

      setCount(countUnreadSince(timestamps, lastRead));
    } catch {
      // Keep prior badge state on transient failures.
    }
  };

  const markRead = async () => {
    setCount(0);
    try {
      const timestamps = await fetchChatMessageTimestamps(options);
      const latest = latestTimestamp(timestamps) ?? new Date().toISOString();
      writeLastReadAt(storageKey, latest);
    } catch {
      writeLastReadAt(storageKey, new Date().toISOString());
    }
  };

  const bumpFromEvent = (createdAt?: unknown) => {
    if (options.isOpen()) {
      setCount(0);
      return;
    }
    const lastRead = readLastReadAt(storageKey);
    if (!lastRead) {
      void refresh();
      return;
    }
    const iso =
      typeof createdAt === "string" ? createdAt : new Date().toISOString();
    if (new Date(iso).getTime() > new Date(lastRead).getTime()) {
      void refresh();
    }
  };

  void refresh();

  const mpUrl = options.maintainerProUrl?.replace(/\/$/, "");
  const apiKey = options.maintainerProApiKey;
  const conversationId = options.conversationId;

  if (mpUrl && apiKey) {
    realtime = startChatRealtime({
      maintainerProUrl: mpUrl,
      apiKey,
      conversationExternalId: conversationId || undefined,
      onMessage: (msg) => {
        if (msg.type === "message.created") {
          const message = msg.message as { createdAt?: string } | undefined;
          bumpFromEvent(message?.createdAt);
        } else if (msg.type === "message.deleted") {
          void refresh();
        }
      },
      onOpen: () => {
        void refresh();
      },
    });
  } else {
    timer = setInterval(() => {
      void refresh();
    }, pollIntervalMs);
  }

  return {
    stop: () => {
      stopped = true;
      if (timer) clearInterval(timer);
      timer = null;
      realtime?.stop();
      realtime = null;
    },
    refresh,
    markRead,
  };
}
